import React, { useEffect, useState } from "react";
import { Linking, Platform, Pressable, StyleSheet, Text, View } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { AppVersionService } from "../../services/AppVersionService";
import { UserRegistrationService } from "../../services/UserRegistrationService";
import { Storage } from "../../services/storage/storage";
import { StorageKeys } from "../../services/storage/keys";
import { Flash } from "../utils/Flash";
import type { AuthStackParamList } from "../../navigation/navigationTypes";
import { environment } from "../../environment";

const normalizePlatformName = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, "");

export function Footer() {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<any>();
  const [appVersion, setAppVersion] = useState("");

  useEffect(() => {
    (async () => {
      const version = environment.appVersion?.trim() || "";
      setAppVersion(version);
      if (version) {
        await Storage.set(StorageKeys.appVersion, version);
      }
    })();
  }, []);

  const handleVersionPress = async () => {
    try {
      const currentVersion = environment.appVersion?.trim() || "";

      const enumerationResponse = await UserRegistrationService.getEnumeration("AppPlatform");
      const platformItems = Array.isArray(enumerationResponse?.data)
        ? enumerationResponse.data
        : [];
      const currentPlatform = normalizePlatformName(Platform.OS);

      const platformEntry = platformItems.find((item: any) => {
        const rawValue = normalizePlatformName(
          item?.value ??
            item?.name ??
            item?.code ??
            item?.displayName ??
            ""
        );

        return (
          rawValue === currentPlatform ||
          (currentPlatform === "ios" && ["iphone", "ipad", "ios"].includes(rawValue)) ||
          (currentPlatform === "android" && ["android", "googleplay"].includes(rawValue)) ||
          (currentPlatform === "web" && ["web", "browser", "website", "desktopweb"].includes(rawValue))
        );
      });

      const platformId = String(platformEntry?.enumerationId ?? platformEntry?.id ?? "");

      if (!platformId || !currentVersion) {
        Flash.error("Version details unavailable", "App version could not be resolved.");
        return;
      }

      const response = await AppVersionService.getVersionDetails({
        platformId,
        currentVersion,
      });

      if (Number(response?.status) !== 200 || !response?.data?.versionNumber) {
        Flash.error("Version details unavailable", response?.message || "Please try again.");
        return;
      }

      navigation.navigate("VersionDetails" satisfies keyof AuthStackParamList, {
        versionNumber: response.data.versionNumber,
        title: response.data.title?.trim() || undefined,
        description: response.data.description?.trim() || undefined,
        createdDateTime: response.data.createdDateTime,
      });
    } catch (error: any) {
      Flash.error("Version details unavailable", error?.message || "Please try again.");
    }
  };

  return (
    <View style={[styles.wrap, { paddingBottom: Math.max(insets.bottom, 8) }]}>
      <Pressable onPress={handleVersionPress} hitSlop={8}>
        <Text style={styles.versionText}>{appVersion ? `Version ${appVersion}` : "Version"}</Text>
      </Pressable>
      <Pressable hitSlop={8} onPress={() => Linking.openURL("https://mindportsystems.com")}>
        <Text style={styles.link}>MINDPORT SYSTEMS LLP</Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    paddingTop: 6,
    marginBottom:5,
    paddingHorizontal: 12,
    backgroundColor: "rgba(255,248,231,0.95)",
    borderTopWidth: 1,
    borderTopColor: "rgba(251,146,60,0.22)",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  versionText: {
    fontSize: 10,
    color: "rgba(124,45,18,0.70)",
    letterSpacing: 0.4,
    fontWeight: "600",
  },
  link: {
    color: "rgba(194,65,12,0.95)",
    fontWeight: "700",
    textDecorationLine: "underline",
    fontSize: 9,
  },
});
